import { ThemeProvider as EmotionThemeProvider, Global, useTheme } from '@emotion/react';
import { ThemeProvider as MuiThemeProvider, useMediaQuery } from '@mui/material';
import { IconContext } from '@phosphor-icons/react';
import { emotionDarkTheme, emotionLightTheme } from '@shared/constants/EmotionTheme';
import { muiDarkTheme, muiLightTheme } from '@shared/constants/MuiTheme';
import { color } from '@shared/constants/Styles';
import { PanelOrientation } from '@shared/models/users/LocalAppSettings';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

interface ThemeContextType {
    theme: 'light' | 'dark';
    setThemeDirectly: (theme: 'light' | 'dark') => void;
    panelPosition: 'left' | 'right' | 'top' | 'bottom';
    setPanelPosition: (position: 'left' | 'right' | 'top' | 'bottom') => void;
}

const defaultThemeContext: ThemeContextType = {
    theme: 'light',
    setThemeDirectly: () => {},
    panelPosition: 'right',
    setPanelPosition: () => {},
};

export const ThemeContext = createContext<ThemeContextType>(defaultThemeContext);

export const GlobalStyles = () => {
    const theme = useTheme();
    return (
        <Global
            styles={{
                body: {
                    color: theme.color.text,
                    ':not(:has(#command-overlay, #panel-overlay))': {
                        backgroundColor: theme.color.background,
                    },
                },
                // for storybook
                '.light-theme': {
                    color: color.primary_dark,
                    backgroundColor: color.bg_light,
                },
                '.dark-theme': {
                    color: color.bg_light,
                    backgroundColor: color.bg_dark,
                },
            }}
        />
    );
};

export const ThemeContextLoader = ({ children }: { children: React.ReactNode }) => {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const [theme, setTheme] = useState<'light' | 'dark'>(prefersDarkMode ? 'dark' : 'light');
    const [panelPosition, setPanelPosition] = useState<'left' | 'right' | 'top' | 'bottom'>('left'); // Default

    useEffect(() => {
        setTheme(prefersDarkMode ? 'dark' : 'light');
    }, [prefersDarkMode]);

    const muiThemeObject = useMemo(() => (theme === 'light' ? muiLightTheme : muiDarkTheme), [theme]);
    const emotionThemeObject = useMemo(
        () => ({
            ...(theme === 'light' ? emotionLightTheme : emotionDarkTheme),
            mode: theme,
            panelPosition,
            panelOrientation: (panelPosition === 'left' || panelPosition === 'right'
                ? 'vertical'
                : 'horizontal') as PanelOrientation,
        }),
        [theme, panelPosition]
    );

    const setThemeDirectly = useCallback((theme: 'light' | 'dark') => {
        setTheme(theme);
    }, []);

    const contextValue = useMemo(
        () => ({
            theme,
            setThemeDirectly,
            panelPosition,
            setPanelPosition,
        }),
        [theme, setThemeDirectly, panelPosition, setPanelPosition]
    );

    return (
        <IconContext.Provider
            value={{
                weight: 'light',
            }}>
            <ThemeContext.Provider value={contextValue}>
                <EmotionThemeProvider theme={emotionThemeObject}>
                    <MuiThemeProvider theme={muiThemeObject}>
                        <GlobalStyles />
                        {children}
                    </MuiThemeProvider>
                </EmotionThemeProvider>
            </ThemeContext.Provider>
        </IconContext.Provider>
    );
};
